/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './totalclicksreport_styles';
import LineChart from 'material/linechart/linechart';


const TOTAL_REACTION_CLICKS = 'Total Clicks' 
const REACTION_ADDED_TITLE = 'Reaction Added %'

const reactionLegendsColors = [
  '#0C1A57', '#F4B442', '#2DBCAB', '#962DBC',
  '#FF191D', '#0E44F6', '#42D6E3', '#FF6FBE'
]
/* ========== ~~~~~~~~~~ ARTICLE SENTIMENT V2 ANALYTICS - REACTION ADDED/TOTAL CLICKS REPORT ~~~~~~~~~~ ========== */
const TotalClicksReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const { mocked } = useSelector(state => state.session)
    const { overview, details, status } = useSelector((state) => state.articleSentimentV2.reactionClicksReport);
    const { overview: reactionClickRateOverview, details: reactionClickRateDetails, status: reactionClickRateStatus } = useSelector((state) => state.articleSentimentV2.reactionClickRateReport);

    const [selectedReport, setSelectedReport] = useState(mocked ? 'reaction-click-rate' : 'reaction-clicks');

    const [showArticleViewsUptick, setShowArticleViewsUptick] = useState(false);
    const [isLoadingReport, loadReport] = useState(false)
    const timerRef = useRef(null);
    const { filters } = useSelector((state) => state.app);
    const  changeType = filters.compareTo;

    // component states
    const [articleViewsUptickLegends, setArticleViewsUptickLegends] = useState([]);
    const isShowReactionClickRate = selectedReport === 'reaction-click-rate';

    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    // component constants
    const totalDatesForInterval = details ? details[selectedInterval] : {};


    const products = [
      { id: 'like', name: 'Like', color: reactionLegendsColors[0] },
      { id: 'dislike', name: 'Dislike', color: reactionLegendsColors[1] },
      { id: 'love', name: 'Love', color: reactionLegendsColors[2] },
      { id: 'funny', name: 'Funny', color: reactionLegendsColors[3] },
      { id: 'angry', name: 'Angry', color: reactionLegendsColors[4] },
      { id: 'sad', name: 'Sad', color: reactionLegendsColors[5] },
      { id: 'shocked', name: 'Shocked', color: reactionLegendsColors[6] },
      { id: 'thoughtful', name: 'Thoughtful', color: reactionLegendsColors[7] },
      { id: 'totalClicks', name: 'Total Clicks', color: '#fff'}
   ]

    const reactionClickRate = [{ id: 'clickRate', name: REACTION_ADDED_TITLE, color: reactionLegendsColors[0] }];

    const totalIndex = products.map(product => product.id).indexOf('totalClicks');


    const dates = details ? details[selectedInterval] : {};

    // show aggregate total on load of page
    useEffect(() => {
        if(status === 'fulfilled' && overview) showAggregateTotal();
    }, [overview]);

    // show article views uptick on load of page
    useEffect(() => {
        if(reactionClickRateStatus === 'fulfilled' && reactionClickRateOverview)  showArticleViewsUptickProductsTotal();
    }, [reactionClickRateOverview]);

  // when mouse hover leaves the chart show back the net total aggregate
  useEffect(() => {
    if (showArticleViewsUptick) {
      if (isShowReactionClickRate) showArticleViewsUptickProductsTotal();
      else showAggregateTotal();
    }
  }, [showArticleViewsUptick]);



    useEffect(() => {
      // Clear the interval when the component unmounts
      return () => clearTimeout(timerRef.current);
    }, []);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if(status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details);
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [status]);


    const computeTableTotal = (products, checkedOnly = true) => {
        // Compute total depending on the selected products
        const total = products.filter(product => product.id !== 'totalClicks' && (checkedOnly ? (product.checked === true) : true))
                            .map(product => product.value)
                            .reduce((prev, current) => prev + current, 0);
        return total;
    }

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}ChangeRate`] : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        productArr[totalIndex] = {
            ...productArr[totalIndex],
            value: computeTableTotal(productArr)
        }

        setChartLegends(productArr);    
    };


    const renderBarChartLegends = () => {
        const totalValue = computeTableTotal(chartLegends, false);

        return chartLegends.map((legend, index) => {
            const productPercentage = totalValue > 0 ? ((legend.value ? legend.value : 0) / totalValue) * 100 : 0;
            const productPercentageText = totalValue ? '(' + formatNumber(productPercentage, 'percent') + ')' : '';
            return (
                <ChartLegend
                    title={`${
                        legend.id === 'total' ? TOTAL_REACTION_CLICKS : legend.name
                    } ${chartLegends.length > 2 && legend.id !== 'totalClicks' ? productPercentageText : ''}`}
                    checkboxColor={legend.color}
                    checked={legend.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }

                        legends[totalIndex] = {
                            ...legends[totalIndex],
                            value: computeTableTotal(legends)
                        }

                        setChartLegends(legends);
                    }}
                    value={formatNumber(legend.value, 'number')}
                    changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                    changeType={changeType}
                    metricType={'trending'}
                />
            )
        })        
    }

    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = {date: formattedDate}
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if(checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                    item[product.name] = detailsDateData[product.id];
                    item[`${product.name}Color`] = product.color;
                    
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if(productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                changeRate = productDetails[`${product.id}ChangeRate`]  ? productDetails[`${product.id}ChangeRate`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        legends[totalIndex] = {
            ...legends[totalIndex],
            value: computeTableTotal(legends)
        }

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
        setShowArticleViewsUptick(false);
    }

      /**
   * show aggregate total for article views uptick
   */
  const showArticleViewsUptickProductsTotal = () => {
    const productArr = reactionClickRate.map((product) => ({
      ...product,
      value: Object.keys(reactionClickRateOverview || []).length > 0 ? reactionClickRateOverview.period[product.id] : 0,
      changeRate: Object.keys(reactionClickRateOverview || []).length > 0 ? reactionClickRateOverview.period[`${product.id}Change`] : 0,
      checked:
        articleViewsUptickLegends.length > 0
          ? articleViewsUptickLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setArticleViewsUptickLegends(productArr);
  };


    const transformArticleViewsUptickLineData = () => {
        const lineChartData = [];
        const dateLength = Object.keys(totalDatesForInterval).length;
        if (reactionClickRateDetails && reactionClickRateDetails[selectedInterval]) {
          const checkedProducts = articleViewsUptickLegends.filter((product) => product.checked === true);
          const items = [];
          reactionClickRate.forEach((product) => {
            if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
              Object.keys(reactionClickRateDetails[selectedInterval]).forEach((date, index) => {
                const value = reactionClickRateDetails[selectedInterval][date].clickRate;
                // To allow duplicate x-axis labels
                let spaces = '';
                for (let i = 0; i < index; i++) spaces += ' ';
                const detailsDateData = reactionClickRateDetails[selectedInterval][date];
                items.push({
                  x: formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true).concat(spaces),
                  y: value,
                  origDate: date,
                  formattedDate: formatProductAnalyticsTableDate(
                    date,
                    selectedInterval,
                    detailsDateData.startDate,
                    detailsDateData.endDate,
                  ),
                  productName: REACTION_ADDED_TITLE,
                });
              });
    
              lineChartData.push({
                id: product.id,
                color: product.color,
                data: items,
              });
            }
          });
        }
        return lineChartData;
      };

      const renderArticleViewsUptickLineChartLegends = () => {
        return articleViewsUptickLegends.map((legend, index) => {
          return (
            <ChartLegend
              title={`${legend.name}`}
              checkboxColor={legend.color}
              checked={legend.checked}
              onCheck={(checked) => {
                const legends = [...articleViewsUptickLegends];
                legends[index] = {
                  ...legends[index],
                  checked: checked,
                };
                setArticleViewsUptickLegends(legends);
              }}
              value={formatNumber(legend.value, 'percent')}
              changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
              change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
              changeType={changeType}
              metricType={'commenting'}
            />
          );
        });
      };
    


    const handleMouseLeave = () => {
        setShowTotal(true);
        setShowArticleViewsUptick(true)
        setSelectedPeriodX('');
    }

    const getBarChartKeys = () =>
        isShowReactionClickRate
          ? reactionClickRate.map((product) => product.name).reverse()
          : products.map((product) => product.name).reverse();
    
      const getBarChartColors = () =>
        isShowReactionClickRate
          ? reactionClickRate.map((product) => product.color).reverse()
          : products.map((product) => product.color).reverse();


    const totalClicksBarChart = {
        indexBy: 'date',
        data: isShowReactionClickRate ? transformArticleViewsUptickLineData() : transformBarChartData(),
        keys:  getBarChartKeys(),
        colors: getBarChartColors(),
        legends: isShowReactionClickRate ? renderArticleViewsUptickLineChartLegends() : renderBarChartLegends()
    }


    const handleSelectReport = (report) => {
        loadReport(true);
        setSelectedReport(report);
        timerRef.current = setTimeout(() => loadReport(false), 1500);
    };

    const cardtoggles = (
		<CardToggles>
            {mocked && <CardToggle
                label={REACTION_ADDED_TITLE}
                compareType={changeType}
                metricType={'trending'}
                percentChange={{
                    value: reactionClickRateOverview?.period ? formatNumber(reactionClickRateOverview?.period?.clickRate, 'percent') : 0,
                    direction: reactionClickRateOverview?.period ? getChangeRateDirection(reactionClickRateOverview?.period?.clickRate) : 'up'
                }}
                selected={selectedReport === 'reaction-click-rate'}
                handleClick={() => handleSelectReport('reaction-click-rate')}
                value={overview?.period ? formatNumber(reactionClickRateOverview?.period?.clickRate, 'percent') : ''}
            />}
            <CardToggle
                label={TOTAL_REACTION_CLICKS}
                compareType={changeType}
                metricType={'trending'}
                percentChange={{
                    value: overview?.period ? formatNumber(overview.period.totalClicks, 'percent') : 0,
                    direction: overview?.period ? getChangeRateDirection(overview.period.totalClicks) : 'up'
                }}
                selected={selectedReport === 'reaction-clicks'}
                handleClick={() => handleSelectReport('reaction-clicks')}

                value={overview?.period ? formatNumber(overview.period.totalClicks) : ''}
            />
		</CardToggles>
	);

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={totalClicksBarChart.colors}
            keys={totalClicksBarChart.keys}
            data={totalClicksBarChart.data}
            period={selectedInterval}
            loading={status === 'pending' || isLoadingReport ? true : false}
            onHover={data => handleHoverOnBarChart(data)}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            barTotalKey={'Total Clicks'}
        />
    );

    const handleHoverArticleViewsUptickLineChart = (lineChartData) => {
        const date = lineChartData.data ? lineChartData.data.origDate : lineChartData.origDate;
        const legends = [...articleViewsUptickLegends];
        reactionClickRate.forEach((product) => {
          let value = 0;
          let changeRate = 0;
          const productDetails = reactionClickRateDetails[selectedInterval][date] ? reactionClickRateDetails[selectedInterval][date] : null;
          if (productDetails) {
            value = productDetails[product.id] ? productDetails[product.id] : 0;
            changeRate = productDetails[`${product.id}Change`] ? productDetails[`${product.id}Change`] : 0;
          }
    
          const legendIndex = articleViewsUptickLegends.map((product) => product.id).indexOf(product.id);
          legends[legendIndex] = {
            ...legends[legendIndex],
            value,
            changeRate,
          };
        });
        setArticleViewsUptickLegends(legends);
        setSelectedPeriodX(`${lineChartData.data ? lineChartData.data.formattedDate : lineChartData.formattedDate}`);
        setShowArticleViewsUptick(false);
      };

      
    const formattedData = transformArticleViewsUptickLineData();
    const hasSingleData = () => {
      let singleData = false;
      formattedData.forEach((item) => {
        if (item.data.length === 1) singleData = true;
      });
      return singleData;
    };

    const lineChart = (
        <LineChart
          category={'product'}
          enablePoints={hasSingleData()}
          data={formattedData}
          onHover={handleHoverArticleViewsUptickLineChart}
          onMouseLeave={handleMouseLeave}
          reset={showArticleViewsUptick}
          yType={'avg-in-view'}
          useDashedLines={true}
          period={selectedInterval}
          isPercentageData={true}
          loading={status === 'pending' ? true : false}
        />
      );
    

    const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'trending'}
            products={isShowReactionClickRate ? reactionClickRate : products}
            data={isShowReactionClickRate ? (reactionClickRateDetails ? reactionClickRateDetails[selectedInterval] : null) : (details ? details[selectedInterval] : null)}
            csvFileName={isShowReactionClickRate ? REACTION_ADDED_TITLE : TOTAL_REACTION_CLICKS}
            dataType={isShowReactionClickRate ? 'percent' : 'number'}
        />
    ) : []

    const isNoData = () => {
        return  (status === 'rejected') ||
                (status === 'fulfilled' && !overview?.period?.totalClicks) ||
                (status === 'fulfilled' && Object.keys(overview).length === 0);
    }

    const tooltip = useMemo(() => {
      if(selectedReport === 'reaction-click-rate') 
        return {
          title: REACTION_ADDED_TITLE,
          content: `Displays the percentage increase in article reaction attributed to the presence of the Article Sentiment unit on the page.`
        }
      if(selectedReport === 'reaction-clicks') 
        return {
          title: TOTAL_REACTION_CLICKS,
          content: `Indicates the total number of additional article reaction resulting from user clicks on a ‘popular’ article in the Article Sentiment unit.`
        }
    }, [selectedReport])

    return (
        <div name={'totalclicksreport'} class={`${props.classes.totalclicksreport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={`${REACTION_ADDED_TITLE} / ${TOTAL_REACTION_CLICKS}`}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>{tooltip.title}</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>{tooltip.content}</p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={isShowReactionClickRate ? lineChart : barchart}
                noData={isNoData()}
                enabledPeriods={enabledRollups}
                chartLegend={totalClicksBarChart.legends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default TotalClicksReport;
